import { useState } from 'react';
import type { Drafts, Vessel } from '../types';
import { calculateDraftSurvey, type DirectHydroValues } from '../lib/calculations';
import { generateSurveyPDF, downloadBlob } from '../lib/pdf-export';
import { ArrowRight, Ruler, Ship, FileText, Download, AlertTriangle, ChevronLeft } from 'lucide-react';

interface QuickSurveyProps {
  onBack: () => void;
}

export default function QuickSurvey({ onBack }: QuickSurveyProps) {
  const [vesselName, setVesselName] = useState('');
  const [lbp, setLbp] = useState('');
  const [drafts, setDrafts] = useState<Drafts>({
    fwd_port: 0, fwd_stbd: 0,
    mid_port: 0, mid_stbd: 0,
    aft_port: 0, aft_stbd: 0,
    density: 1.025
  });
  const [hydro, setHydro] = useState({ displacement: '', tpc: '', mtc: '', lcf: '', dmtc: '' });
  const [result, setResult] = useState<ReturnType<typeof calculateDraftSurvey> | null>(null);
  const [error, setError] = useState('');

  const updateDraft = (field: keyof Drafts, val: string) => {
    setDrafts((prev) => ({ ...prev, [field]: val === '' ? 0 : parseFloat(val) }));
  };

  const handleCalc = () => {
    setError('');
    try {
      const lbpNum = parseFloat(lbp);
      if (!lbpNum || lbpNum <= 0) throw new Error('LBP is required');
      if (!vesselName.trim()) throw new Error('Vessel name is required');
      if (!hydro.displacement || !hydro.tpc || !hydro.mtc || !hydro.lcf) {
        throw new Error('All 4 hydrostatic values are required (displacement, TPC, MTC, LCF)');
      }

      const directHydro: DirectHydroValues = {
        displacement: parseFloat(hydro.displacement),
        tpc: parseFloat(hydro.tpc),
        mtc: parseFloat(hydro.mtc),
        lcf: parseFloat(hydro.lcf),
        dmtc: hydro.dmtc ? parseFloat(hydro.dmtc) : undefined,
      };

      const tempVessel: Vessel = {
        id: 'quick-' + crypto.randomUUID(),
        name: vesselName.trim(),
        type: 'general_cargo',
        lbp: lbpNum,
        hydrostatic: [],
        tank_tables: [],
        constants: { bunkers: 0, fw: 0, ballast: 0, stores: 0, slops: 0, others: 0 },
        created_at: new Date().toISOString(),
      };

      const res = calculateDraftSurvey(drafts, tempVessel, directHydro);
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Calculation failed');
    }
  };

  const handleExport = () => {
    if (!result) return;
    const tempVessel: Vessel = {
      id: 'quick-' + crypto.randomUUID(),
      name: vesselName.trim(),
      type: 'general_cargo',
      lbp: parseFloat(lbp),
      hydrostatic: [],
      tank_tables: [],
      constants: { bunkers: 0, fw: 0, ballast: 0, stores: 0, slops: 0, others: 0 },
      created_at: new Date().toISOString(),
    };
    const tempSurvey = {
      id: 'quick-' + crypto.randomUUID(),
      vessel_id: 'quick-' + crypto.randomUUID(),
      survey_type: 'initial' as const,
      method: 'displacement' as const,
      survey_date: new Date().toISOString(),
      density: drafts.density,
      drafts,
      corrections: result.corrections,
      displacement: result.displacement,
      constants: { bunkers: 0, fw: 0, ballast: 0, stores: 0, slops: 0, others: 0 },
      status: 'completed' as const,
      created_at: new Date().toISOString(),
    };
    const blob = generateSurveyPDF(tempVessel, tempSurvey, result.details, result.warnings);
    downloadBlob(blob, `QuickSurvey_${vesselName.trim().replace(/\s+/g, '_')}_${Date.now()}.pdf`);
  };

  const stations = [
    { key: 'fwd_port' as const, label: 'Fwd Port' },
    { key: 'fwd_stbd' as const, label: 'Fwd Stbd' },
    { key: 'mid_port' as const, label: 'Mid Port' },
    { key: 'mid_stbd' as const, label: 'Mid Stbd' },
    { key: 'aft_port' as const, label: 'Aft Port' },
    { key: 'aft_stbd' as const, label: 'Aft Stbd' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <button type="button" onClick={onBack} className="text-sm text-navy-400 hover:text-navy-200 flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="glass rounded-xl p-4 flex items-center gap-3">
        <Ship className="w-5 h-5 text-ocean-400" />
        <div>
          <h2 className="text-lg font-bold text-navy-100">Quick Survey</h2>
          <p className="text-xs text-navy-400">No vessel needed. Enter hydrostatic values directly.</p>
        </div>
      </div>

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-3 text-xs text-rose-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Vessel Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="card-premium p-3">
          <label className="label-premium text-xs">Vessel Name *</label>
          <input className="input-premium" value={vesselName} onChange={(e) => setVesselName(e.target.value)} placeholder="e.g. COMANAV Atlas" />
        </div>
        <div className="card-premium p-3">
          <label className="label-premium text-xs">LBP (m) *</label>
          <input type="number" step="0.01" className="input-premium" value={lbp} onChange={(e) => setLbp(e.target.value)} placeholder="Length Between Perpendiculars" />
        </div>
      </div>

      {/* Draft Readings */}
      <div className="glass rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Ruler className="w-4 h-4 text-ocean-400" />
          <h3 className="text-sm font-bold text-navy-200">Draft Readings</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {stations.map((s) => (
            <div key={s.key} className="card-premium p-3">
              <label className="label-premium text-xs">{s.label}</label>
              <input type="number" step="0.001" className="input-premium" value={drafts[s.key] || ''} onChange={(e) => updateDraft(s.key, e.target.value)} placeholder="0.000" />
            </div>
          ))}
          <div className="card-premium p-3">
            <label className="label-premium text-xs">Density</label>
            <input type="number" step="0.001" className="input-premium" value={drafts.density || ''} onChange={(e) => updateDraft('density', e.target.value)} placeholder="1.025" />
          </div>
        </div>
      </div>

      {/* Hydrostatic Values */}
      <div className="glass rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-ocean-400" />
          <h3 className="text-sm font-bold text-navy-200">Hydrostatic Values at Observed Draft</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="card-premium p-3">
            <label className="label-premium text-xs">Displacement (MT) *</label>
            <input type="number" step="0.01" className="input-premium" value={hydro.displacement} onChange={(e) => setHydro(p => ({ ...p, displacement: e.target.value }))} placeholder="0.00" />
          </div>
          <div className="card-premium p-3">
            <label className="label-premium text-xs">TPC *</label>
            <input type="number" step="0.01" className="input-premium" value={hydro.tpc} onChange={(e) => setHydro(p => ({ ...p, tpc: e.target.value }))} placeholder="0.00" />
          </div>
          <div className="card-premium p-3">
            <label className="label-premium text-xs">MTC *</label>
            <input type="number" step="0.01" className="input-premium" value={hydro.mtc} onChange={(e) => setHydro(p => ({ ...p, mtc: e.target.value }))} placeholder="0.00" />
          </div>
          <div className="card-premium p-3">
            <label className="label-premium text-xs">LCF (m) *</label>
            <input type="number" step="0.01" className="input-premium" value={hydro.lcf} onChange={(e) => setHydro(p => ({ ...p, lcf: e.target.value }))} placeholder="+ = aft, − = fwd" />
          </div>
          <div className="card-premium p-3">
            <label className="label-premium text-xs">δMTC (optional)</label>
            <input type="number" step="0.01" className="input-premium" value={hydro.dmtc} onChange={(e) => setHydro(p => ({ ...p, dmtc: e.target.value }))} placeholder="For 2nd correction" />
          </div>
        </div>
        <p className="text-[10px] text-navy-500 mt-2">
          Read these values directly from the hydrostatic table at the observed mean draft. LCF sign: + = aft of midship, − = fwd (European convention).
        </p>
      </div>

      <button type="button" onClick={handleCalc} className="btn-primary w-full flex items-center justify-center gap-2">
        Calculate Displacement <ArrowRight className="w-4 h-4" />
      </button>

      {/* Results */}
      {result && (
        <div className="space-y-4 animate-fade-in">
          <div className="glass rounded-2xl p-6 text-center border-ocean-500/20">
            <p className="text-xs uppercase tracking-widest text-navy-400 mb-2">Displacement</p>
            <div className="text-4xl sm:text-5xl font-bold text-gradient font-mono tracking-tight">
              {result.displacement.toFixed(3)}
            </div>
            <p className="text-sm text-ocean-400 mt-1 font-medium">Metric Tons (MT)</p>
          </div>

          {result.warnings.length > 0 && (
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 text-rose-400 font-bold text-sm">
                <AlertTriangle className="w-4 h-4" /> Warnings
              </div>
              {result.warnings.map((w, i) => (
                <p key={i} className="text-xs text-rose-300 pl-6">• {w}</p>
              ))}
            </div>
          )}

          <div className="card-premium">
            <div className="p-4 border-b border-navy-700/50 flex items-center gap-2">
              <FileText className="w-4 h-4 text-navy-400" />
              <h3 className="font-bold text-navy-200 text-sm">Calculation Log</h3>
            </div>
            <div className="p-4 max-h-64 overflow-y-auto scrollbar-hide space-y-1 font-mono text-xs">
              {result.details.map((d, i) => (
                <div key={i} className={`py-1 px-2 rounded ${d.includes('TOTAL') || d.includes('CARGO') || d.includes('Final') || d.includes('Quarter Mean') || d.includes('ALWAYS') || d.includes('δMTC') ? 'bg-ocean-500/10 text-ocean-300 font-bold' : 'text-navy-400'}`}>
                  {d}
                </div>
              ))}
            </div>
          </div>

          <button type="button" onClick={handleExport} className="btn-primary w-full flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Export PDF Report
          </button>
        </div>
      )}
    </div>
  );
}
